import { getAttribution } from "./attribution";

const COOKIE_CONSENT_KEY = "cookie-consent";
const SESSION_ID_KEY = "analytics-session-id";

// Generate or get session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

// Check if tracking is allowed
export const isTrackingAllowed = (): boolean => {
  return localStorage.getItem(COOKIE_CONSENT_KEY) === "accepted";
};

// Get device info
const getDeviceInfo = () => {
  const ua = navigator.userAgent;
  let deviceType = "desktop";
  let browser = "unknown";
  let os = "unknown";

  if (/mobile/i.test(ua)) deviceType = "mobile";
  else if (/tablet/i.test(ua)) deviceType = "tablet";

  if (/firefox/i.test(ua)) browser = "firefox";
  else if (/chrome/i.test(ua)) browser = "chrome";
  else if (/safari/i.test(ua)) browser = "safari";
  else if (/edge/i.test(ua)) browser = "edge";

  if (/windows/i.test(ua)) os = "windows";
  else if (/mac/i.test(ua)) os = "macos";
  else if (/linux/i.test(ua)) os = "linux";
  else if (/android/i.test(ua)) os = "android";
  else if (/ios/i.test(ua)) os = "ios";

  return { deviceType, browser, os };
};

interface TrackEventParams {
  eventType: string;
  eventData?: Record<string, any>;
  formData?: Record<string, any>;
}

// Lazy-load supabase only when actually tracking
const getSupabase = () =>
  import("@/integrations/supabase/client").then(m => m.supabase);

// Track event
export const trackEvent = async ({
  eventType,
  eventData,
  formData,
}: TrackEventParams) => {
  if (!isTrackingAllowed()) {
    return;
  }

  try {
    const supabase = await getSupabase();
    const { deviceType, browser, os } = getDeviceInfo();
    const { data: { user } } = await supabase.auth.getUser();

    const attribution = getAttribution();
    const payload = {
      session_id: getSessionId(),
      event_type: eventType,
      page_url: window.location.href,
      page_title: document.title,
      referrer: document.referrer || null,
      user_agent: null,
      device_type: deviceType,
      browser: browser,
      os: os,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      event_data: eventData || null,
      form_data: formData || null,
      user_id: user?.id || null,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      gclid: attribution.gclid,
    };

    await supabase.from("analytics_events").insert(payload);
  } catch (error) {
    console.error("Analytics tracking error:", error);
  }
};

// Track page view
export const trackPageView = () => {
  trackEvent({ eventType: "page_view" });
};

// Track click
export const trackClick = (elementId: string, elementText?: string) => {
  trackEvent({
    eventType: "click",
    eventData: { element_id: elementId, element_text: elementText },
  });
};

// Google Ads Conversion-Tracking
// AW-ID
const AW_ID = 'AW-18074792283';

// Pro Conversion-Typ ein eigenes Label in Google Ads anlegen und hier eintragen.
// Solange für einen Typ noch kein eigenes Label existiert, fällt der Code auf
// das "umbrella"-Label zurück, damit nichts verloren geht.
const FALLBACK_LABEL = 'yYgbCNyH7pgcENvi3apD';
const CONVERSION_LABELS: Record<string, string> = {
  contact_form: FALLBACK_LABEL,
  whatsapp_click: FALLBACK_LABEL,
  phone_click: FALLBACK_LABEL,
};

export const trackConversion = (conversionLabel: string, value?: number) => {
  if (typeof window.gtag !== 'function') return;
  const label = CONVERSION_LABELS[conversionLabel] || FALLBACK_LABEL;
  window.gtag('event', 'conversion', {
    send_to: `${AW_ID}/${label}`,
    event_label: conversionLabel,
    ...(value && { value, currency: 'EUR' }),
  });
};

// Track CTA click (more descriptive than generic click)
export const trackCTAClick = (ctaName: string, ctaLocation: string, ctaText?: string) => {
  trackEvent({
    eventType: "cta_click",
    eventData: { cta_name: ctaName, cta_location: ctaLocation, cta_text: ctaText },
  });

  // Auto-trigger Google Ads conversions for key actions
  if (ctaName === 'whatsapp') trackConversion('whatsapp_click');
  if (ctaName === 'phone' || ctaName === 'call') trackConversion('phone_click');

  // Deeplink-Outcome: prüfen, ob Browser den Tab wirklich verlässt
  if (ctaName === 'whatsapp' || ctaName === 'call' || ctaName === 'phone') {
    trackDeeplinkOutcome(ctaName, ctaLocation);
  }
};


// Detect whether a deeplink/external navigation actually succeeded.
// Fires `cta_open_confirmed` when the document becomes hidden / page unloads
// within the window, else `cta_open_unconfirmed`.
const trackDeeplinkOutcome = (ctaName: string, ctaLocation: string) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const WINDOW_MS = 1500;
  let settled = false;

  const cleanup = () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onHide);
    window.removeEventListener('blur', onBlur);
    window.clearTimeout(timer);
  };

  const confirm = (signal: 'visibilitychange' | 'pagehide' | 'blur') => {
    if (settled) return;
    settled = true;
    cleanup();
    trackEvent({
      eventType: 'cta_open_confirmed',
      eventData: { cta_name: ctaName, cta_location: ctaLocation, signal },
    });
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') confirm('visibilitychange');
  };
  const onHide = () => confirm('pagehide');
  const onBlur = () => {
    // window blur often fires when a deeplink hands off to the OS app
    if (document.visibilityState === 'hidden' || !document.hasFocus()) confirm('blur');
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHide);
  window.addEventListener('blur', onBlur);

  const timer = window.setTimeout(() => {
    if (settled) return;
    settled = true;
    cleanup();
    trackEvent({
      eventType: 'cta_open_unconfirmed',
      eventData: { cta_name: ctaName, cta_location: ctaLocation, window_ms: WINDOW_MS },
    });
  }, WINDOW_MS);
};


// Track section view – pro Session+Section nur EINMAL schreiben.
const seenSections = new Set<string>();
export const trackSectionView = (sectionId: string, sectionName: string) => {
  const key = `${getSessionId()}::${sectionName}`;
  if (seenSections.has(key)) return;
  seenSections.add(key);
  trackEvent({
    eventType: "section_view",
    eventData: { section_id: sectionId, section_name: sectionName },
  });
};

// ──────────────────────────────────────────────────────────
// Inquiry-Sheet Funnel Diagnostics
// ──────────────────────────────────────────────────────────

export type InquirySheetCloseReason =
  | "backdrop"
  | "escape"
  | "x_button"
  | "submitted"
  | "navigation"
  | "tab_hidden"
  | "page_unload"
  | "unknown";

export const trackInquirySheetClose = (data: {
  vehicle_type?: string;
  vehicle_label?: string;
  vehicle_id?: string | null;
  time_in_sheet_seconds: number;
  had_field_focus: boolean;
  max_scroll_depth_in_sheet: number;
  close_reason: InquirySheetCloseReason;
  first_field_focused: string | null;
  viewport_h: number;
}) => {
  trackEvent({ eventType: "inquiry_sheet_close", eventData: data });
};

export const trackInquirySheetScroll = (data: {
  depth: 25 | 50 | 75 | 100;
  vehicle_type?: string;
  vehicle_id?: string | null;
}) => {
  trackEvent({ eventType: "inquiry_sheet_scroll", eventData: data });
};

export const trackInquiryTimeToFirstFocus = (data: {
  seconds: number;
  field_name: string;
  vehicle_type?: string;
  vehicle_id?: string | null;
  viewport_h: number;
}) => {
  trackEvent({ eventType: "inquiry_sheet_time_to_first_focus", eventData: data });
};

export const trackInquiryValidationError = (data: {
  field_name: string;
  error_type: string;
  vehicle_type?: string;
}) => {
  trackEvent({ eventType: "inquiry_form_validation_error", eventData: data });
};

// Track sub-page view (industry-specific landing pages)
export const trackSubpageView = (slug: string, industry: string) => {
  trackEvent({
    eventType: "subpage_view",
    eventData: { subpage_slug: slug, subpage_industry: industry },
  });
};

// Track form submission
export const trackFormSubmission = (formName: string, formData: Record<string, any>) => {
  trackEvent({
    eventType: "form_submission",
    formData: { form_name: formName, ...formData },
  });
};

// Track scroll depth – nur 50 % und 90 % schreiben (statt 4 Stufen).
// max_scroll_depth läuft sowieso im page_exit-Event mit.
let maxScrollDepth = 0;
const SCROLL_MILESTONES = [50, 90];
const sentScrollMilestones = new Set<number>();
export const trackScrollDepth = () => {
  const scrollDepth = Math.round(
    (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
  );
  if (scrollDepth > maxScrollDepth) maxScrollDepth = scrollDepth;
  for (const m of SCROLL_MILESTONES) {
    if (scrollDepth >= m && !sentScrollMilestones.has(m)) {
      sentScrollMilestones.add(m);
      trackEvent({ eventType: "scroll_depth", eventData: { depth: m } });
    }
  }
};

// Exit tracking state
let lastSeenSection = "";
let pageLoadTime = Date.now();

export const setLastSeenSection = (sectionId: string) => {
  lastSeenSection = sectionId;
};

export const getExitData = () => ({
  last_section: lastSeenSection,
  max_scroll_depth: maxScrollDepth,
  time_on_page_seconds: Math.round((Date.now() - pageLoadTime) / 1000),
});

// Track exit using sendBeacon for guaranteed delivery
export const trackExit = () => {
  if (!isTrackingAllowed()) return;

  const exitData = getExitData();
  const { deviceType, browser, os } = getDeviceInfo();

  const attribution = getAttribution();
  const payload = {
    session_id: getSessionId(),
    event_type: "page_exit",
    page_url: window.location.href,
    page_title: document.title,
    referrer: document.referrer || null,
    user_agent: null,
    device_type: deviceType,
    browser,
    os,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    event_data: exitData,
    form_data: null,
    user_id: null,
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    gclid: attribution.gclid,
  };

  // Use sendBeacon for reliable delivery on page exit
  const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_events`;
  const headers = {
    "Content-Type": "application/json",
    "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    "Authorization": `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    "Prefer": "return=minimal",
  };

  try {
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    // sendBeacon doesn't support custom headers, so fall back to fetch with keepalive
    fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Silent fail on exit
  }
};

// Reset page load time (call on route change)
export const resetPageTimer = () => {
  pageLoadTime = Date.now();
  maxScrollDepth = 0;
  lastSeenSection = "";
  sentScrollMilestones.clear();
};
